import React from "react";
import { MongoAbility } from "@casl/ability";
import {
  Building2, Users, FileText, Briefcase, GraduationCap, Network, UserCheck, User, TrendingUp, ShieldCheck,
  LayoutGrid, ScrollText, Package, Monitor, CreditCard, Search, Mail, FlaskConical, AlertTriangle, Footprints,
  Contact, Truck, MapPin, CalendarCheck, ClipboardCheck, Settings, Warehouse, Archive, ArrowLeftRight,
  ClipboardList, Container, ShoppingCart, BookOpen, Tags, BarChart3, Gauge, Target
} from "lucide-react";

import { ACTIONS, RESOURCES } from "./permissions.constants";

// Shared navigation registry — single source of truth for the sidebar and
// the m.idarah.io square-apps landing grid. Keep both consumers rendering
// from these helpers so permission rules never drift apart.

export interface NavLink {
  href: string;
  label: string;
  icon: React.ReactNode;
  action?: string;
  subject?: string;
  permissions?: { action: string; subject: string }[];
  /** AND-of-many CASL check: every entry must pass (unlike `permissions`, which is OR-of-many). */
  requiredPermissions?: { action: string; subject: string }[];
  requiresModuleAccess?: boolean;
  requiresOnboarding?: boolean;
  excludedRoles?: string[];
  badge?: string | number;
  /**
   * Nested nodes. A link with `children` is a disclosure group, never a destination:
   * the sidebar renders it as an expandable parent, and m.idarah.io renders its `label`
   * as a sub-heading above its children's tiles. `href` is then the group's primary
   * child — for keys and active state — not something the user navigates to.
   *
   * A group whose children are all permission-filtered away disappears entirely.
   */
  children?: NavLink[];
  /**
   * Highlight only on an exact path match, never on sub-paths.
   *
   * For a section landing page whose href is a prefix of its siblings — /stock sits
   * above /stock/products, /stock/transfers and the rest — the default sub-path rule
   * would leave it lit on every page in the section.
   */
  exact?: boolean;
}

export interface NavCategory {
  category: string | null; // null = no category header (top-level items)
  links: NavLink[];
  /**
   * Category-level CASL gate. The whole section — header and all — is hidden unless
   * this passes, on top of the per-link rules. STOCK uses it so a user without
   * `read:stock` sees no section at all, matching the desktop sidebar.
   */
  requires?: { action: string; subject: string };
}

/** Minimal shape of a stock yard needed to build the STOCK yard nodes. */
export interface NavStockYard {
  id: string | number;
  yardName: string;
  status?: string;
}

// Minimal shape of the authenticated user needed for navigation filtering.
export interface NavUser {
  id?: string | number;
  status?: string;
  userRole?: string;
  showInAttendance?: boolean;
}

/** Minimal service-desk shape needed to build the SERVICE DESKS nodes. */
export interface NavServiceDesk {
  id: string | number;
  name: string;
  teamAdmin?: { id?: string | number } | null;
  members?: { user?: { id?: string | number } | null }[] | null;
}

/**
 * Data the registry needs from outside: the user's id, the service desks they
 * staff, those that have retired, and the stock yards visible to them.
 * `GET /api/stock/yards` is already filtered server-side by `visibleYardIds()`,
 * so whatever comes back is what that user may see — the registry does not
 * re-derive access.
 */
export interface NavDataSource {
  currentUserId?: string | number;
  activeDesks?: NavServiceDesk[];
  retiredDesks?: NavServiceDesk[];
  stockYards?: NavStockYard[];
}

/**
 * Builds the categorized navigation structure. Dynamic entries (service
 * desks, retired desks, stock yards, the My Details deep link, the env-gated
 * DEBUG section) are resolved from `data`.
 */
export const getNavCategories = ({
  currentUserId,
  activeDesks = [],
  retiredDesks = [],
  stockYards = [],
}: NavDataSource = {}): NavCategory[] => {
  const openYards = stockYards.filter(y => y.status !== "closed");
  const closedYards = stockYards.filter(y => y.status === "closed");

  return [
  {
    category: null, // Top-level items (no category header)
    links: [
      {
        href: "/dashboard",
        label: "Dashboard",
        icon: <LayoutGrid size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.DASHBOARD,
      },
    ],
  },
  {
    category: "MY PROFILE",
    links: [
      {
        href: `/people/${currentUserId}`,
        label: "My Details",
        icon: <User size={20} />,
      },
      {
        href: "/my-requests",
        label: "My Requests",
        icon: <User size={20} />,
        action: ACTIONS.MANAGE,
        subject: RESOURCES.SERVICE_DESK_REQUESTER,
      },
      {
        href: "/my-policies",
        label: "My Policies",
        icon: <UserCheck size={20} />,
        permissions: [
          { action: ACTIONS.READ, subject: RESOURCES.POLICY_SIGNATURE },
          { action: ACTIONS.CREATE, subject: RESOURCES.POLICY_SIGNATURE }
        ]
      },
      {
        href: "/my-onboarding",
        label: "My Onboarding",
        icon: <UserCheck size={20} />,
        requiresOnboarding: true,
      },
      {
        href: "/my-learning",
        label: "My Learning",
        icon: <GraduationCap size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.COURSE_ENROLLMENT,
      },
      {
        href: "/my-performance",
        label: "My Performance",
        icon: <Gauge size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PERFORMANCE_MY_GOALS,
      },
      // {
      //   href: "/development",
      //   label: "Development",
      //   icon: <TrendingUp size={20} />,
      //   action: ACTIONS.READ,
      //   subject: RESOURCES.DELEGATION_DEVELOPMENT,
      // },
    ],
  },
  {
    category: "MY TASKS",
    links: [
      {
        href: "/safety/observations",
        label: "Safety Observations",
        icon: <ClipboardCheck size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.SAFETY_MY_TASKS,
      },
    ],
  },
  {
    category: "HR",
    links: [
      {
        href: "/hr",
        label: "HR Dashboard",
        icon: <BarChart3 size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.HR_DASHBOARD,
      },
      {
        href: "/performance-settings",
        label: "Performance Settings",
        icon: <Target size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PERFORMANCE_SCHEDULE,
      },
      {
        href: "/organization",
        label: "Organization",
        icon: <Network size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.ORGANISATION_CHART,
      },
      {
        href: "/departments",
        label: "Departments",
        icon: <Building2 size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.DEPARTMENT_LIST,
      },
      {
        href: "/people",
        label: "People",
        icon: <Users size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PERSON_PAGE,
        permissions: [
          { action: ACTIONS.READ, subject: RESOURCES.PERSON_PAGE },
          { action: ACTIONS.READ, subject: RESOURCES.PERSON_I_MANAGE_PAGE }
        ]
      },
      {
        href: "/job-roles",
        label: "Job Roles",
        icon: <Briefcase size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.JOB_ROLE_LIST,
      },
      {
        href: "/training-plans",
        label: "Training",
        icon: <GraduationCap size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.TRAINING_PLAN_LIST,
      },
      {
        // Learning (LEARNING.md §8). A disclosure group, not a destination: the sidebar
        // expands it into the four sub-areas, and each child carries its own grant so a
        // role that can only read the register still gets the group. `filterNavLinks`
        // drops the group entirely when every child is filtered away.
        href: "/learning/courses",
        label: "Learning",
        icon: <GraduationCap size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.LEARNING_ADMIN,
        children: [
          {
            href: "/learning/courses",
            label: "Course Library",
            icon: <BookOpen size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.COURSE,
            exact: true,
          },
          {
            href: "/learning/register",
            label: "Learning Register",
            icon: <ClipboardList size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.COURSE_ASSIGNMENT,
          },
          {
            href: "/learning/categories",
            label: "Categories",
            icon: <Tags size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.COURSE_CATEGORY,
          },
          {
            href: "/learning-admin/_brief",
            label: "Eng & Permissions",
            icon: <ShieldCheck size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.LEARNING_ADMIN,
          },
          {
            // The notification configuration centre (LEARNING.md §16). Its own grant so who
            // may tune the emails is independent of who may build courses or assign them.
            href: "/learning/settings",
            label: "Settings",
            icon: <Settings size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.LEARNING_SETTINGS,
          },
        ],
      },
      {
        href: "/qualifications",
        label: "Qualifications",
        icon: <ScrollText size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.QUALIFICATION,
      },
      {
        href: "/attendance",
        label: "Attendance & Leave",
        icon: <CalendarCheck size={20} />,
        requiresModuleAccess: true
      },
      {
        href: "/recruitment",
        label: "Recruitment",
        icon: <Search size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.RECRUITMENT_MODULE,
      },
      {
        href: "/onboarding",
        label: "Onboarding",
        icon: <UserCheck size={20} />,
        // The dashboard shows the employees in onboarding, so require the
        // request-list permission as well — converted hires with dashboard
        // access alone must not see it (their onboarding view is My Onboarding).
        requiredPermissions: [
          { action: ACTIONS.READ, subject: RESOURCES.ONBOARDING_DASHBOARD },
          { action: ACTIONS.READ, subject: RESOURCES.ONBOARDING_REQUEST_LIST },
        ],
      },
    ],
  },
  {
    category: "SERVICE DESKS",
    links: [
      {
        href: "/service-desk/admin",
        label: "Admin",
        icon: <ShieldCheck size={20} />,
        action: ACTIONS.MANAGE,
        subject: RESOURCES.SERVICE_DESK_ADMIN,
      },
      ...activeDesks.map(d => {
        const link: NavLink = {
          href: `/service-desk/${d.id}`,
          label: d.name,
          icon: <Users size={20} />,
        };

        const userIdStr = String(currentUserId);

        // Only show if user is explicit staff (Team Admin, Agent, Viewer)
        const isStaff = d.teamAdmin?.id && String(d.teamAdmin.id) === userIdStr ||
                        d.members?.some(m => m.user?.id && String(m.user.id) === userIdStr);

        if (isStaff) {
          return link;
        }

        return null;
      }).filter(Boolean) as NavLink[],
      // Retired desks are an archive: admins only (the same grant as Admin), rendered
      // as a collapsible group so the tree stays flat for everyone else.
      ...(retiredDesks.length > 0 ? [{
        href: `/service-desk/${retiredDesks[0].id}`,
        label: "Retired Desks",
        icon: <Package size={20} />,
        action: ACTIONS.MANAGE,
        subject: RESOURCES.SERVICE_DESK_ADMIN,
        badge: retiredDesks.length,
        children: retiredDesks.map(d => ({
          href: `/service-desk/${d.id}`,
          label: d.name,
          icon: <Users size={20} />,
        })),
      }] : []),
      // The desk settings page is a sibling of Admin, not a tab inside it — without its
      // own node a mobile admin has no way to reach it (desktop had it sidebar-only).
      {
        href: "/service-desk/settings",
        label: "Settings",
        icon: <Settings size={20} />,
        action: ACTIONS.MANAGE,
        subject: RESOURCES.SERVICE_DESK_ADMIN,
      },
    ],
  },
  {
    category: "OPERATIONS",
    links: [
      {
        href: "/procedure",
        label: "Procedure",
        icon: <FileText size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PROCEDURE_LIST,
      },
      {
        href: "/procedures",
        label: "Procedures (legacy)",
        icon: <FileText size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PROCEDURE_LIST,
      },
      {
        href: "/policies",
        label: "Policies",
        icon: <ScrollText size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.POLICY,
        excludedRoles: ["Field Worker"]
      },
      {
        href: "/inventory",
        label: "Uniform & PPE",
        icon: <ShieldCheck size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.INVENTORY_ITEM,
      },
      {
        href: "/assets",
        label: "Assets",
        icon: <Package size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.ASSET,
      },
    ],
  },
  {
    category: "LISTS",
    links: [
      {
        href: "/lists/customers",
        label: "Customers",
        icon: <Contact size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.LIST_CUSTOMER,
      },
      {
        href: "/lists/vendors",
        label: "Vendors",
        icon: <Truck size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.LIST_VENDOR,
      },
      {
        href: "/lists/branches",
        label: "Branches",
        icon: <MapPin size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.LIST_BRANCH,
      },
      {
        href: "/lists/projects",
        label: "Projects",
        icon: <Briefcase size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.LIST_PROJECT,
      },
    ],
  },
  {
    // STOCK (STOCK-MENU.md §1). Yard nodes are data-driven — `GET /api/stock/yards` is
    // filtered server-side to the caller's memberships — and every node carries the same
    // CASL grant the desktop sidebar enforced, so the m.idarah.io grid cannot drift from it.
    category: "STOCK",
    requires: { action: ACTIONS.READ, subject: RESOURCES.STOCK },
    links: [
      {
        href: "/stock",
        label: "Dashboard",
        icon: <LayoutGrid size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.STOCK_DASHBOARD,
        // Every other STOCK route is /stock/something — without this the Dashboard
        // stays lit across the whole section.
        exact: true,
      },
      ...openYards.map(yard => ({
        href: `/stock/yards/${yard.id}/deliveries`,
        label: yard.yardName,
        icon: <Warehouse size={20} />,
        children: [
          {
            href: `/stock/yards/${yard.id}/deliveries`,
            label: "Deliveries",
            icon: <Truck size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.STOCK_DELIVERY_ORDER,
          },
          {
            href: `/stock/yards/${yard.id}/returns`,
            label: "Returns",
            icon: <ClipboardList size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.STOCK_RETURN_ORDER,
          },
          {
            href: `/stock/yards/${yard.id}/imports`,
            label: "Import Orders",
            icon: <Container size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.STOCK_IMPORT_ORDER,
          },
        ],
      })),
      {
        href: "/stock/transfers",
        label: "Transfer Orders",
        icon: <ArrowLeftRight size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.STOCK_TRANSFER_ORDER,
      },
      {
        href: "/stock/products",
        label: "Product Library",
        icon: <Package size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.STOCK_PRODUCT,
      },
      {
        // §4 notification workflow — sits outside the per-yard subtree beside Product
        // Library, and outside the admins-only Settings group, because the Yard Managers
        // and Transport Managers who run the delivery flow are the ones who tune it.
        href: "/stock/settings/deliveries",
        label: "Delivery Settings",
        icon: <Truck size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.STOCK_DELIVERY_SETTINGS,
      },
      {
        // Procurement → Purchase Orders. Branch-scoped rather than yard-scoped: AP raises
        // the PO against a branch, so the node sits outside the per-yard subtree.
        href: "/stock/procurement",
        label: "Purchase Orders",
        icon: <ShoppingCart size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.PURCHASE_ORDER,
        children: [
          {
            href: "/stock/procurement",
            label: "Dashboard",
            icon: <LayoutGrid size={20} />,
            exact: true,
          },
          {
            href: "/stock/procurement/purchase-orders",
            label: "All Purchase Orders",
            icon: <ClipboardList size={20} />,
          },
          {
            href: "/stock/procurement/approvals",
            label: "Approvals",
            icon: <ClipboardCheck size={20} />,
            // Approving is a separate grant from reading the queue's contents; without it
            // the page would render nothing actionable, so hide the node entirely.
            action: ACTIONS.APPROVE,
            subject: RESOURCES.PURCHASE_ORDER_APPROVAL,
          },
          {
            href: "/stock/procurement/receiving",
            label: "Receiving",
            icon: <Package size={20} />,
            action: ACTIONS.CREATE,
            subject: RESOURCES.PURCHASE_ORDER_RECEIPT,
          },
          {
            href: "/stock/procurement/settings",
            label: "Settings",
            icon: <Settings size={20} />,
            action: ACTIONS.READ,
            subject: RESOURCES.PURCHASE_ORDER_SETTINGS,
          },
        ],
      },
      {
        // §5.3: Settings is admins only — hidden entirely for everyone else.
        href: "/stock/settings/yards",
        label: "Settings",
        icon: <Settings size={20} />,
        action: ACTIONS.MANAGE,
        subject: RESOURCES.STOCK_ADMIN,
        children: [
          {
            href: "/stock/settings/yards",
            label: "Yards",
            icon: <Warehouse size={20} />,
          },
        ],
      },
      // §4: hidden entirely when the user has no closed yards to show.
      ...(closedYards.length > 0 ? [{
        href: "/stock/closed-yards",
        label: "Closed Yards",
        icon: <Archive size={20} />,
        badge: closedYards.length,
        children: [
          {
            href: "/stock/closed-yards",
            label: "All closed yards",
            icon: <Archive size={20} />,
          },
          ...closedYards.map(yard => ({
            href: `/stock/closed-yards/${yard.id}/returns`,
            label: yard.yardName,
            icon: <Warehouse size={20} />,
          })),
        ],
      }] : []),
    ],
  },
  {
    category: "SAFETY",
    links: [
      {
        href: "/safety/hazardous-chemicals",
        label: "Hazardous Chemicals",
        icon: <FlaskConical size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.HAZARDOUS_CHEMICAL,
      },
      {
        href: "/safety/incidents",
        label: "Incidents & Injuries",
        icon: <AlertTriangle size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.INCIDENT_INJURY,
      },
      {
        href: "/safety/walks",
        label: "Safety Walks",
        icon: <Footprints size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.SAFETY_WALK_LIST,
      },
    ],
  },
  {
    category: "IT",
    links: [
      {
        href: "/business-systems",
        label: "Business Systems",
        icon: <Monitor size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.BUSINESS_SYSTEMS_MODULE,
      },
    ],
  },
  {
    category: "FINANCE",
    links: [
      {
        href: "/finance/direct-debit",
        label: "Direct Debit Register",
        icon: <CreditCard size={20} />,
        action: ACTIONS.READ,
        subject: RESOURCES.DIRECT_DEBIT_MODULE,
      },
    ],
  },
  // Conditionally include DEBUG category
  ...(process.env.REACT_APP_DEBUG_EMAIL_SERVICE === 'true' ? [{
    category: "DEBUG",
    links: [
      {
        href: "/service-desk/email-templates",
        label: "Email Templates",
        icon: <Mail size={20} />,
      },
    ],
  }] : []),
  ];
};

export interface NavFilterContext {
  ability: MongoAbility;
  currentUser: NavUser | null;
  hasOnboarding: boolean;
}

/**
 * Decides whether a navigation link should be rendered for the current
 * user. This is the single implementation of the sidebar's historical
 * `canRenderLink` rules:
 * - Onboarding users only see links flagged `requiresOnboarding`.
 * - `requiresModuleAccess` needs `currentUser.showInAttendance`.
 * - `requiresOnboarding` needs an existing onboarding request.
 * - `excludedRoles` hides the link for those user roles.
 * - `permissions` is an OR-of-many CASL check; `requiredPermissions` is an
 *   AND-of-many CASL check; a single `action`/`subject` pair is a single
 *   CASL check; links with neither render for everyone.
 */
export const canRenderNavLink = (link: NavLink, ctx: NavFilterContext): boolean => {
  const { ability, currentUser, hasOnboarding } = ctx;

  // Onboarding users only see the My Onboarding portal link.
  if (currentUser?.status === "Onboarding") {
    return link.requiresOnboarding === true;
  }
  if (link.requiresModuleAccess && !currentUser?.showInAttendance) {
    return false;
  }
  if (link.requiresOnboarding && !hasOnboarding) {
    return false;
  }
  if (link.excludedRoles && currentUser?.userRole && link.excludedRoles.includes(currentUser.userRole)) {
    return false;
  }
  if (
    link.requiredPermissions &&
    !link.requiredPermissions.every(p => ability.can(p.action, p.subject as any))
  ) {
    return false;
  }
  if (link.permissions) {
    return link.permissions.some(p => ability.can(p.action, p.subject as any));
  } else if (link.action && link.subject) {
    return ability.can(link.action, link.subject as any);
  }
  return true; // No action/subject defined, allow all users
};

/**
 * Returns the subset of `links` visible to the current user, recursing into group
 * children and dropping a group whose children are all hidden — a header with nothing
 * under it is noise on both the sidebar and the m.idarah.io grid.
 */
export const filterNavLinks = (links: NavLink[], ctx: NavFilterContext): NavLink[] =>
  links
    .map(link => link.children
      ? { ...link, children: filterNavLinks(link.children, ctx) }
      : link)
    .filter(link => canRenderNavLink(link, ctx) && (!link.children || link.children.length > 0));

/** Whether a whole category — header included — should render for the current user. */
export const canRenderNavCategory = (category: NavCategory, ctx: NavFilterContext): boolean =>
  !category.requires || ctx.ability.can(category.requires.action, category.requires.subject);
